import { PublicClientApplication, AuthError, BrowserAuthError } from '@azure/msal-browser';
import React, { useState, useEffect, useContext, createContext, useRef } from 'react';
import { CustomerService } from 'api';
import createAxiosInstanceWithToken from 'api/init';
import { getUserDataFromSession } from 'utils/account/getUserDataFromSession';
import MSAL_CONFIG, { AUTHORIZATION_REQUEST, getMsalForgotPasswordConfig, FORGOT_PASSWORD_CODE, CANCEL_BUTTON_CODE } from './msalConfig';
export const MSALContext = /*#__PURE__*/createContext({});
const msal = new PublicClientApplication(MSAL_CONFIG);
const IDP_ACCESS_TOKEN_CLAIM = 'idp_access_token';
export const MSALProvider = ({
  children
}) => {
  const [account, setAccount] = useState();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const refreshTokenPromise = useRef(null);
  const refreshAccessTokenSilentlyRef = useRef(async () => null);
  const axiosInitialized = useRef(false);
  refreshAccessTokenSilentlyRef.current = refreshAccessTokenSilently;
  const getAccount = () => {
    const currentAccounts = msal.getAllAccounts();
    if (currentAccounts.length) {
      const [firstAccount] = currentAccounts;
      return firstAccount;
    }
    return null;
  };
  const login = async loginRequest => {
    try {
      await msal.loginRedirect(loginRequest);
    } catch (e) {
      if (e instanceof BrowserAuthError) {
        // Do nothing, emits "interaction in progress error"
      }
    }
  };
  const completeAuthentication = (bearerToken, accountInfo) => {
    setToken(bearerToken);
    setIsAuthenticated(true);
    setAccount(accountInfo);
    setLoading(false);
  };
  const hasIdpAccessToken = idTokenClaims => Boolean(idTokenClaims?.[IDP_ACCESS_TOKEN_CLAIM]);
  const fetchAndStoreUserData = async accessToken => {
    const userData = getUserDataFromSession();
    if (userData) return;
    try {
      const response = await CustomerService.getPersonData({
        body: {
          Token: accessToken
        }
      });
      sessionStorage.setItem('userData', JSON.stringify(response));
    } catch {
      // Ignore user-data prefill failures; auth flow should continue.
    }
  };
  const applyAuthenticatedSession = async ({
    accessToken,
    accountInfo,
    idTokenClaims,
    forceUserDataRefresh = false
  }) => {
    if (!axiosInitialized.current) {
      createAxiosInstanceWithToken(accessToken, refreshAccessTokenSilently);
      axiosInitialized.current = true;
    }
    if (forceUserDataRefresh) {
      sessionStorage.removeItem('userData');
    }
    if (hasIdpAccessToken(idTokenClaims)) {
      await fetchAndStoreUserData(accessToken);
    }
    completeAuthentication(accessToken, accountInfo);
  };
  const redirectToAcquireToken = accountInfo => msal.acquireTokenRedirect({
    scopes: AUTHORIZATION_REQUEST.scopes,
    account: accountInfo
  });
  async function refreshAccessTokenSilently() {
    if (refreshTokenPromise.current) return refreshTokenPromise.current;
    const acc = getAccount();
    if (!acc) return null;
    refreshTokenPromise.current = (async () => {
      try {
        const res = await msal.acquireTokenSilent({
          scopes: AUTHORIZATION_REQUEST.scopes,
          account: acc,
          forceRefresh: false
        });
        if (!res.accessToken) return null;
        await applyAuthenticatedSession({
          accessToken: res.accessToken,
          accountInfo: acc,
          expiresOn: res.expiresOn
        });
        return res.accessToken;
      } catch {
        redirectToAcquireToken(acc);
        return null;
      }
    })();
    try {
      return await refreshTokenPromise.current;
    } finally {
      refreshTokenPromise.current = null;
    }
  }
  const getTokenRedirect = async () => {
    const acc = getAccount();
    if (!acc) {
      login(AUTHORIZATION_REQUEST);
      return;
    }
    const loginRequest = {
      scopes: AUTHORIZATION_REQUEST.scopes,
      account: acc
    };
    try {
      const res = await msal.acquireTokenSilent(loginRequest);
      if (!res.accessToken) {
        await msal.ssoSilent({
          ...loginRequest,
          loginHint: acc.username
        });
      } else {
        await applyAuthenticatedSession({
          accessToken: res.accessToken,
          accountInfo: acc,
          expiresOn: res.expiresOn,
          idTokenClaims: res.idTokenClaims
        });
      }
    } catch {
      redirectToAcquireToken(acc);
    }
  };
  const handleAuth = async () => {
    try {
      const response = await msal.handleRedirectPromise();
      if (response) {
        const acc = getAccount();
        if (!acc) {
          login(AUTHORIZATION_REQUEST);
          return;
        }
        if (response.accessToken) {
          await applyAuthenticatedSession({
            accessToken: response.accessToken,
            accountInfo: acc,
            expiresOn: response.expiresOn,
            idTokenClaims: response.idTokenClaims,
            forceUserDataRefresh: true
          });
        }
      } else {
        getTokenRedirect();
      }
    } catch (error) {
      if (error instanceof AuthError) {
        // Redirects to forgot password page.

        // TODO: Does not work to click the back button
        if (error.message.indexOf(FORGOT_PASSWORD_CODE) > -1) {
          const forgotPasswordMsal = new PublicClientApplication(getMsalForgotPasswordConfig());
          forgotPasswordMsal.loginRedirect(AUTHORIZATION_REQUEST);
        } else if (error.message.indexOf(CANCEL_BUTTON_CODE) > -1) {
          getTokenRedirect();
        }
      }
    }
  };
  useEffect(() => {
    const init = async () => {
      await handleAuth();
      sessionStorage.setItem('customer-redirected', 'false');
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const logout = () => {
    msal.logoutRedirect({
      account
    });
    setAccount(undefined);
    sessionStorage.removeItem('userData');
  };
  return /*#__PURE__*/React.createElement(MSALContext.Provider, {
    value: {
      logout,
      getTokenRedirect,
      account,
      token,
      loading,
      isAuthenticated
    }
  }, children);
};
export const useMSAL = () => useContext(MSALContext);